import type * as PdfjsNS from "pdfjs-dist";
/* `?url` makes Vite resolve the file inside pdfjs-dist, emit it as an asset and
 * hand back the served URL. A bare specifier inside `new URL(..., import.meta.url)`
 * is NOT resolved — Vite rewrites it relative to this module, producing a path
 * that 404s. Since pdfjs-dist v4 the worker is an ES module, so the extension is
 * .mjs; pdf.js spawns it with `type: "module"`. This import only yields a string,
 * so the worker itself is not downloaded until pdf.js actually starts it. */
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

export const PDFJS_WORKER_URL = pdfWorkerUrl;

type Pdfjs = typeof PdfjsNS;

let pending: Promise<Pdfjs> | null = null;

/**
 * Loads pdf.js on first use and caches the result.
 *
 * pdf.js is ~970 KB, and most sessions never open a PDF — importing it eagerly
 * put it on the critical path of every page, including login. Awaiting this at
 * the point of use keeps it off the initial load.
 *
 * Concurrent callers share one in-flight import. `window.pdfjsLib` is still set
 * once resolved, because legacy code reads it from there.
 */
export function ensurePdfjs(): Promise<Pdfjs> {
  if (!pending) {
    pending = import("pdfjs-dist")
      .then((mod) => {
        const lib = mod as unknown as Pdfjs;
        lib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
        if (typeof window !== "undefined") {
          (window as Window & { pdfjsLib?: Pdfjs }).pdfjsLib = lib;
        }
        return lib;
      })
      .catch((err) => {
        /* Let the next caller retry rather than caching the failure. */
        pending = null;
        throw err;
      });
  }
  return pending;
}

/** Synchronous accessor. Returns null until `ensurePdfjs()` has resolved. */
export function getPdfjsLib(): Pdfjs | null {
  if (typeof window === "undefined") return null;
  return (window as Window & { pdfjsLib?: Pdfjs }).pdfjsLib ?? null;
}
